import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import RegularButton from 'components/CustomButtons/Button';
import ReactTable from 'components/ReactTable/ReactTableV2';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import ModalBase from 'components/Tvendo/Common/ModalBase';
import Badge from 'components/Badge/Badge';
import { wsFindBy } from 'services/WService';
import { nDecimal } from 'hooks/Pipe';
import moment from 'moment';

const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 60 },
  { Header: 'Estado', accessor: 'estado', width: 110, excelEnabled: false },
  { Header: 'Estado', accessor: 'estado_string', width: 110, hidden: true },
  { Header: 'Tipo', accessor: 'tipo', width: 110 },
  { Header: 'Serie', accessor: 'serie', width: 90 },
  { Header: 'Número', accessor: 'number', width: 110 },
  { Header: 'RUC / DNI', accessor: 'ruc', width: 130 },
  { Header: 'Código', accessor: 'card_code', width: 110 },
  { Header: 'Razón Social', accessor: 'partner_name' },
  { Header: 'Emisión', accessor: 'doc_date', width: 110 },
  { Header: 'Total', accessor: 'total', width: 120 },
  { Header: 'Anulado', accessor: 'canceled', width: 90 },
];

const RECIBO_HONORARIO = '02';

const TIPOS_DOC = [
  { value: 'T', label: 'Todos' },
  { value: 'R', label: 'Recibo por honorario' },
  { value: 'D', label: 'Documentos' },
];

export default function SireMissingModal(props) {
  const { model, setOpen, entity, title, filter } = props;

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [buscado, setBuscado] = useState(false);
  const [tipoDoc, setTipoDoc] = useState('T');

  const visibles = useMemo(() => {
    if (tipoDoc === 'R') return records.filter((r) => r.document_type === RECIBO_HONORARIO);
    if (tipoDoc === 'D') return records.filter((r) => r.document_type !== RECIBO_HONORARIO);
    return records;
  }, [records, tipoDoc]);

  const dias = desde && hasta ? moment(hasta).diff(moment(desde), 'days') + 1 : 0;
  const excedido = dias > 31;

  const desdeMin = hasta ? moment(hasta).subtract(30, 'days').format('YYYY-MM-DD') : undefined;
  const hastaMax = desde ? moment(desde).add(30, 'days').format('YYYY-MM-DD') : undefined;

  useEffect(() => {
    if (!model.open) return;

    const fin = filter?.end_date || moment().format('YYYY-MM-DD');
    let ini = filter?.start_date || moment(fin).subtract(7, 'days').format('YYYY-MM-DD');

    if (moment(fin).diff(moment(ini), 'days') + 1 > 31) {
      ini = moment(fin).subtract(30, 'days').format('YYYY-MM-DD');
    }

    setDesde(ini);
    setHasta(fin);
    setRecords([]);
    setError('');
    setBuscado(false);
    setTipoDoc('T');
  }, [model.open]);

  async function buscar() {
    if (excedido) return;

    setLoading(true);
    setError('');
    const res = await wsFindBy(`${entity}/no-declarados`, { start_date: desde, end_date: hasta });
    setLoading(false);
    setBuscado(true);

    if (res.success) {
      setRecords(res.data || []);
    } else {
      setRecords([]);
      setError(res.message || 'No se pudo consultar SAP');
    }
  }

  return (
    <ModalBase
      title={(<>{title} {loading && <LoadingSpinner />}</>)}
      model={model}
      setOpen={setOpen}
      hideSave
      fullWidth
      maxWidth="lg"
    >
      <GridContainer>
        <GridItem xs={12} md={3}>
          <TextField
            label="Desde"
            type="date"
            fullWidth
            margin="normal"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: desdeMin, max: hasta }}
          />
        </GridItem>
        <GridItem xs={12} md={3}>
          <TextField
            label="Hasta"
            type="date"
            fullWidth
            margin="normal"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: desde, max: hastaMax }}
          />
        </GridItem>
        <GridItem xs={12} md={2}>
          <FormControl fullWidth margin="normal">
            <InputLabel shrink>Tipo</InputLabel>
            <Select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)}>
              {TIPOS_DOC.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem xs={12} md={4} style={{ paddingTop: 28 }}>
          <RegularButton color="primary" disabled={loading || excedido || !desde || !hasta} onClick={buscar}>
            {loading ? 'Buscando...' : 'Buscar'}
          </RegularButton>
          {excedido && (
            <span style={{ marginLeft: 12, color: '#c62828' }}>
              Máximo 31 días.
            </span>
          )}
        </GridItem>

        <GridItem xs={12}>
          {!!error && <p style={{ color: '#c62828' }}>{error}</p>}

          {buscado && !loading && records.length === 0 && !error && (
            <p style={{ color: '#2e7d32' }}>
              Todos los comprobantes de SAP del rango están en el SIRE.
            </p>
          )}

          {records.length > 0 && visibles.length === 0 && (
            <p>No hay comprobantes de ese tipo sin declarar en el rango.</p>
          )}

          {visibles.length > 0 && (
            <>
              {tipoDoc !== 'T' && (
                <p style={{ fontSize: 13, color: '#666' }}>
                  {visibles.length} de {records.length}
                </p>
              )}
              <ReactTable
                excelName={`sap_no_declarado_${entity.split('/')[0]}`}
                formatDate="DD-MM-YYYY"
                defaultWidth={1400}
                columns={columns}
                data={visibles.map((item, i) => ({
                  num: i + 1,
                  estado: (
                    <Badge color={item.estado === 'POSIBLE' ? 'warning' : 'danger'}>
                      {item.estado === 'POSIBLE' ? 'Posible' : 'Falta'}
                    </Badge>
                  ),
                  estado_string: item.estado === 'POSIBLE' ? 'Posible' : 'Falta',
                  tipo:
                    {
                      '01': 'Factura',
                      '02': 'Recibo Hon.',
                      '03': 'Boleta',
                      '07': 'N. Crédito',
                      '08': 'N. Débito',
                    }[item.document_type] || item.document_type,
                  serie: item.serie,
                  number: item.number,
                  ruc: item.ruc || '-',
                  card_code: item.card_code,
                  partner_name: item.partner_name,
                  doc_date: item.doc_date,
                  total: nDecimal(item.total),
                  canceled: item.canceled === 'Y' ? 'Sí' : 'No',
                }))}
              />
            </>
          )}
        </GridItem>
      </GridContainer>
    </ModalBase>
  );
}
